import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, History } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { elAPI, elErr } from '../../../lib/elAPI';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU') : '—');

const PdfDialog = ({ url, title, onClose }) => (
  <Dialog open={!!url} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-4xl"><DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      {url && (navigator.pdfViewerEnabled === false
        ? <a href={url} target="_blank" rel="noreferrer" download="clause-preview.pdf" className="text-sm text-primary underline">Open / download the preview PDF</a>
        : <iframe title="Clause preview" src={url} className="h-[75vh] w-full rounded border" data-testid="clause-preview-frame" />)}
    </DialogContent>
  </Dialog>
);

const HistoryDialog = ({ c, open, onClose, preview }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid={`clause-history-${c.key}`}><DialogHeader><DialogTitle>{c.title} – history</DialogTitle></DialogHeader>
      <div className="space-y-1 text-sm">
        {c.versions.map((v) => (
          <div key={v.version} className="flex items-center justify-between border-b py-1.5 last:border-0">
            <span>Version {v.version} · {fmt(v.created_at)}{v.version === c.current_version ? ' · in use' : ''}</span>
            <Button size="sm" variant="ghost" onClick={() => preview(v.version)} data-testid={`clause-history-preview-${c.key}-${v.version}`}>Preview</Button>
          </div>))}
        <p className="pt-2 text-xs text-muted-foreground">Letters already issued keep the exact wording they were sent with.</p>
      </div>
    </DialogContent>
  </Dialog>
);

const ClauseEditor = ({ c, reload }) => {
  const [body, setBody] = useState(c.current.body);
  const [pdf, setPdf] = useState(null);
  const [hist, setHist] = useState(false);
  const dirty = body !== c.current.body;
  const preview = async (version) => {
    try { const b = await elAPI.previewBlob(c.key, 'generic', version); setPdf(URL.createObjectURL(new Blob([b], { type: 'application/pdf' }))); } catch (e) { toast.error(elErr(e)); }
  };
  const save = () => elAPI.addVersion(c.key, { body, note: '' }).then(() => { toast.success('Saved – new letters use this wording'); reload(); }).catch((e) => toast.error(elErr(e)));
  return (
    <Card data-testid={`clause-card-${c.key}`}>
      <PdfDialog url={pdf} title={`${c.title} – preview`} onClose={() => { URL.revokeObjectURL(pdf); setPdf(null); }} />
      <HistoryDialog c={c} open={hist} onClose={() => setHist(false)} preview={preview} />
      <CardHeader className="flex flex-row items-center justify-between gap-4 pb-2">
        <CardTitle className="text-base">{c.title}</CardTitle>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => preview(c.current_version)} data-testid={`clause-preview-${c.key}`}><Eye className="mr-1 h-3.5 w-3.5" />Preview</Button>
          <Button size="sm" variant="ghost" onClick={() => setHist(true)} data-testid={`clause-history-link-${c.key}`}><History className="mr-1 h-3.5 w-3.5" />History</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="text-xs leading-relaxed" data-testid={`clause-text-${c.key}`} />
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Updated {fmt(c.updated_at)}. Variables: {'{{firm.legal_name}}'}, {'{{firm.trading_name}}'}{c.key === 'offshore' ? ', {{offshore.countries}}, {{offshore.providers}}' : ''}.</p>
          {dirty && <div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => setBody(c.current.body)}>Discard</Button><Button size="sm" onClick={save} data-testid={`clause-save-${c.key}`}>Save</Button></div>}
        </div>
      </CardContent>
    </Card>
  );
};

export const ClausesPanel = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => elAPI.clauses().then(setData).catch((e) => toast.error(elErr(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="el-clauses-panel">
      <p className="text-sm text-muted-foreground">These two clauses are included in every engagement letter and disclosure addendum, just before the signature block. Signers tick to acknowledge AI use (required) and to authorise offshore services (optional).</p>
      {data.clauses.map((c) => <ClauseEditor key={`${c.key}-${c.current_version}`} c={c} reload={load} />)}
    </div>
  );
};
